import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 100px auto 4rem;
  padding: 0 2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};

  h1 {
    font-size: 2.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 2rem;
    text-align: center;
    position: relative;
    &::after {
      content: '';
      display: block;
      width: 80px;
      height: 4px;
      background-color: ${props => props.theme.colors.secondary};
      margin: 1rem auto 0;
    }
  }

  h2 {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 3rem;
    margin-bottom: 1rem;
    position: relative;
    padding-bottom: 0.5rem;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 50px;
      height: 3px;
      background-color: ${props => props.theme.colors.secondary};
    }
  }

  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 2rem;
    margin-bottom: 0.8rem;
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: disc;
    margin-left: 20px;
    margin-bottom: 1.5rem;
    li {
      margin-bottom: 0.5rem;
    }
  }

  a {
    color: ${props => props.theme.colors.secondary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsOfService = () => {
  return (
    <PageContainer
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h1>Terms of Service</h1>
      <p><strong>Last Updated: December 29, 2025</strong></p>
      <p>Welcome to L&J Agape Healthcare Ltd. These Terms of Service ("Terms") govern your access to and use of our website, services, and all related content provided by L&J Agape Healthcare Ltd ("we," "us," or "our"). By accessing or using our services, you agree to be bound by these Terms.</p>

      <h2>1. Acceptance of Terms</h2>
      <p>By accessing and using the website of L&J Agape Healthcare Ltd, you confirm that you accept these Terms and agree to comply with them. If you do not agree to these Terms, you must not use our website or services.</p>

      <h2>2. Services Offered</h2>
      <p>L&J Agape Healthcare Ltd is a provider of compassionate and professional care services. Our services are primarily offered across the <strong>West Midlands, United Kingdom</strong>, and include:</p>
      <ul>
        <li><strong>Dementia Care:</strong> Specialized support for individuals living with dementia.</li>
        <li><strong>Personal Care:</strong> Assistance with daily activities such as bathing, dressing, and medication management.</li>
        <li><strong>Mental Health Support:</strong> Compassionate care for individuals with mental health conditions.</li>
        <li><strong>Disability Care:</strong> Specialized support for individuals with physical or learning disabilities.</li>
        <li><strong>Elderly Care:</strong> Comprehensive support for older adults to maintain independence and dignity.</li>
        <li><strong>Live-In Care (24/7):</strong> Round-the-clock dedicated care within the client's home.</li>
        <li><strong>Agency Staffing:</strong> Provision of qualified and reliable healthcare professionals to care homes and other healthcare settings.</li>
        <li><strong>Care Professionals:</strong> Connecting skilled and compassionate care professionals directly with clients.</li>
      </ul>
      <p>Details of each service, including specific inclusions and client responsibilities, are available on our website or through direct consultation.</p>

      <h2>3. Eligibility</h2>
      <p>Our services are available to individuals and organizations who are legally capable of entering into binding contracts. For direct care services, clients must typically be 18 years of age or older, or have a legal guardian/representative acting on their behalf. For Agency Staffing and Care Professionals, applicants must meet all legal and professional requirements for employment in the UK healthcare sector.</p>

      <h2>4. Service Provision and Care Plans</h2>
      <ul>
        <li><strong>Assessment:</strong> All direct care services begin with a comprehensive assessment to understand the client's unique needs, preferences, and circumstances.</li>
        <li><strong>Personalized Care Plans:</strong> Based on the assessment, a personalized care plan will be developed in collaboration with the client and, where appropriate, their family or representatives.</li>
        <li><strong>Professional Staff:</strong> We commit to providing qualified, compassionate, and appropriately trained staff for all our services, adhering to industry standards and regulatory requirements in the West Midlands.</li>
        <li><strong>Client Cooperation:</strong> Clients and their representatives are expected to cooperate fully with our staff, provide accurate and complete information relevant to care, and ensure a safe working environment for our care professionals.</li>
      </ul>

      <h2>5. Payment Terms</h2>
      <ul>
        <li><strong>Fees:</strong> Our fees for services will be communicated clearly and agreed upon prior to the commencement of services.</li>
        <li><strong>Invoicing:</strong> Invoices will be issued regularly as per the agreed schedule.</li>
        <li><strong>Payment:</strong> Payments are due by the date specified on the invoice. We reserve the right to charge interest on overdue payments as per statutory regulations.</li>
        <li><strong>Changes to Fees:</strong> We reserve the right to review and adjust our service fees. Any changes will be communicated in advance.</li>
      </ul>

      <h2>6. Cancellations and Changes</h2>
      <ul>
        <li><strong>Client Cancellation:</strong> Clients wishing to cancel or modify services must provide notice as per the terms outlined in their individual service agreement. Cancellation fees may apply depending on the notice period.</li>
        <li><strong>Service Changes:</strong> We will endeavor to accommodate requests for changes to care plans. Significant changes may require a revised assessment and agreement.</li>
        <li><strong>Provider Cancellation:</strong> We reserve the right to cancel or suspend services in exceptional circumstances, such as non-payment, unsafe environment, or inability to provide appropriate care. Reasonable notice will be given where possible.</li>
      </ul>

      <h2>7. Client Responsibilities</h2>
      <p>Clients agree to:</p>
      <ul>
        <li>Provide accurate and complete information regarding their needs.</li>
        <li>Ensure a safe and respectful environment for our staff.</li>
        <li>Communicate any changes in health, medication, or living situation promptly.</li>
        <li>Adhere to agreed payment terms.</li>
      </ul>

      <h2>8. Our Responsibilities</h2>
      <p>We commit to:</p>
      <ul>
        <li>Providing high-quality, compassionate, and professional care.</li>
        <li>Ensuring our staff are appropriately qualified, trained, and vetted.</li>
        <li>Maintaining client confidentiality in accordance with our Privacy Policy and data protection laws.</li>
        <li>Responding promptly to client inquiries and concerns.</li>
        <li>Continuously reviewing and improving our services.</li>
      </ul>

      <h2>9. Intellectual Property</h2>
      <p>All content on this website, including text, graphics, logos, images, and software, is the property of L&J Agape Healthcare Ltd or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.</p>

      <h2>10. Limitation of Liability</h2>
      <p>To the fullest extent permitted by law, L&J Agape Healthcare Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your access to or use of or inability to access or use the services; (b) any conduct or content of any third party on the services; or (c) unauthorized access, use, or alteration of your transmissions or content.</p>

      <h2>11. Governing Law</h2>
      <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>

      <h2>12. Dispute Resolution</h2>
      <p>We aim to resolve any concerns or complaints amicably. If you have a complaint regarding our services, please contact us directly at <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a>. We commit to addressing your concerns promptly and fairly. If a resolution cannot be reached informally, further legal avenues may be pursued.</p>

      <h2>13. Changes to These Terms</h2>
      <p>We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. By continuing to access or use our services after those revisions become effective, you agree to be bound by the revised terms.</p>

      <h2>14. Contact Information</h2>
      <p>If you have any questions about these Terms, please contact us:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a></li>
        <li><strong>Phone:</strong> +44 7874 290538</li>
        <li><strong>Address:</strong> L&J Agape Healthcare Ltd, 1-3 Market Place, Willenhall, WV13 2AA, United Kingdom</li>
      </ul>
    </PageContainer>
  );
};

export default TermsOfService;